import React, { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../supabase'

const BUCKET = 'loan-documents'

export default function LoanDocuments({ loan, userId, onClose }) {
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef(null)

  const prefix = `${userId}/${loan.id}`

  const loadFiles = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase.storage.from(BUCKET).list(prefix, { sortBy: { column: 'created_at', order: 'desc' } })
    if (error) { setError('Could not load documents: ' + error.message) }
    else setFiles(data || [])
    setLoading(false)
  }, [prefix])

  useEffect(() => { loadFiles() }, [loadFiles])

  async function handleUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 10 * 1024 * 1024) { setError('File must be under 10 MB'); return }
    setUploading(true)
    setError('')
    const path = `${prefix}/${Date.now()}_${file.name}`
    const { error } = await supabase.storage.from(BUCKET).upload(path, file)
    if (error) setError('Upload failed: ' + error.message)
    else await loadFiles()
    setUploading(false)
    e.target.value = ''
  }

  async function handleDelete(name) {
    if (!window.confirm(`Delete "${name}"?`)) return
    const { error } = await supabase.storage.from(BUCKET).remove([`${prefix}/${name}`])
    if (error) setError('Delete failed: ' + error.message)
    else setFiles(prev => prev.filter(f => f.name !== name))
  }

  async function handleDownload(name) {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(`${prefix}/${name}`, 300)
    if (error) { setError('Could not generate download link'); return }
    window.open(data.signedUrl, '_blank')
  }

  function fmtSize(bytes) {
    if (!bytes) return ''
    if (bytes >= 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB'
    return (bytes / 1024).toFixed(0) + ' KB'
  }

  function displayName(name) {
    return name.replace(/^\d+_/, '')
  }

  function fileIcon(name) {
    const ext = name.split('.').pop().toLowerCase()
    if (['pdf'].includes(ext)) return '📄'
    if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) return '🖼'
    if (['doc', 'docx'].includes(ext)) return '📝'
    if (['xls', 'xlsx'].includes(ext)) return '📊'
    return '📎'
  }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
    >
      <div style={{ background: 'var(--surface)', borderRadius: '10px', border: '1px solid var(--border)', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700' }}>Loan Documents</div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>{loan.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: '1.25rem 1.5rem' }}>
          {error && (
            <div style={{ background: 'rgba(184,68,26,0.08)', border: '1px solid rgba(184,68,26,0.2)', borderRadius: '6px', padding: '0.65rem 0.9rem', marginBottom: '1rem', fontSize: '0.78rem', color: '#b8441a', fontFamily: 'monospace' }}>
              ⚠ {error}
            </div>
          )}

          <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleUpload}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.xls,.xlsx" />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {loading ? 'Loading...' : `${files.length} document${files.length !== 1 ? 's' : ''}`}
            </span>
            <button
              onClick={() => fileInputRef.current.click()}
              disabled={uploading}
              style={{ background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.4rem 0.85rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.68rem', fontWeight: '500', cursor: uploading ? 'not-allowed' : 'pointer', textTransform: 'uppercase', letterSpacing: '0.04em', opacity: uploading ? 0.6 : 1 }}
            >
              {uploading ? 'Uploading...' : '↑ Upload'}
            </button>
          </div>

          {!loading && files.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2.5rem 1rem', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📂</div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', marginBottom: '0.3rem' }}>No documents yet</div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.62rem' }}>Upload PDFs, images, or Office files</div>
            </div>
          )}

          {files.map(file => (
            <div key={file.name} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 0.9rem', borderRadius: '6px', border: '1px solid var(--border)', marginBottom: '0.5rem', background: 'var(--surface-hdr)' }}>
              <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{fileIcon(file.name)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName(file.name)}</div>
                {file.metadata?.size && (
                  <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)' }}>{fmtSize(file.metadata.size)}</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0 }}>
                <button
                  onClick={() => handleDownload(file.name)}
                  style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: '3px', padding: '0.2rem 0.5rem', fontFamily: 'monospace', fontSize: '0.6rem', cursor: 'pointer', color: 'var(--text-muted)' }}
                  onMouseEnter={e => { e.target.style.borderColor = 'var(--text)'; e.target.style.color = 'var(--text)' }}
                  onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text-muted)' }}
                >
                  ↓ View
                </button>
                <button
                  onClick={() => handleDelete(file.name)}
                  style={{ background: 'transparent', border: '1px solid rgba(184,68,26,0.25)', borderRadius: '3px', padding: '0.2rem 0.5rem', fontFamily: 'monospace', fontSize: '0.6rem', cursor: 'pointer', color: '#b8441a' }}
                  onMouseEnter={e => e.target.style.borderColor = '#b8441a'}
                  onMouseLeave={e => e.target.style.borderColor = 'rgba(184,68,26,0.25)'}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}

          <div style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'var(--text-muted)', marginTop: '0.75rem', lineHeight: 1.5 }}>
            Supported: PDF, DOC/DOCX, JPG/PNG/WebP, XLS/XLSX · Max 10 MB per file
          </div>
        </div>
      </div>
    </div>
  )
}
